'use es6';

import { List } from 'immutable';
import PropTypes from 'prop-types';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { ReportingIngestionInteraction } from 'reporting-data/monitoring/ingestion';
import { isOnlySupportedViaBackendResolve } from 'reporting-data/v2/reportingApi/isSupportedForBackendResolve';
import { reportsEqual } from 'reporting-ui-components/lib/shallowCompareReports';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import configure, { fixFilters } from '../../decorators/ConfigureDecorator/configure';
import { loadResolve } from '../../decorators/DataDecorator/loadResolve';
import { isEnabledForViz } from '../isReportingVizEnabled';
import ReportWithData from './ReportWithData';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { FiscalYearContext } from '../../../components/PlatformReport/FiscalYearProvider';
import { ReportingLogger } from 'reporting-data/monitoring/reportingLogger';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const REPORT_WITH_RESOLVE_ERROR_PAGEACTION = 'ReportWithResolveError';
const ReportWithResolve = ({
  report,
  runtimeOptions = {},
  disableEmptyStates,
  paginationRenderer,
  noDataRenderer,
  errorRenderer,
  onReportResolve,
  onError,
  setIsRendered,
  visualizationDebug,
  resolve: customResolve,
  determineVisualization,
  isMobile,
  visualize,
  refreshReportData,
  compact,
  mobileReportingOptions,
  vizContainerStyleProps,
  disableZoom
}) => {
  const resolveId = useRef();
  const [reportState, setReportState] = useState({});
  const [previousReportState, setPreviousReportState] = useState({});
  const {
    report: currentReport
  } = reportState;
  const reportIngestion = useRef();
  const {
    gates
  } = useContext(UserInfoContext);
  const fiscalYearMonthName = useContext(FiscalYearContext);
  useEffect(() => {
    /**
     * Temporarily ignore functions when comparing report changes
     * https://git.hubteam.com/HubSpot/Critsit/issues/1330
     */
    if (currentReport && reportsEqual(currentReport, report)) {
      return;
    }
    setReportState({
      report,
      reportLoading: true
    });
    reportIngestion.current = reportIngestion.current || new ReportingIngestionInteraction(report, {
      enabledForViz: isEnabledForViz(report)
    });
    resolveId.current = resolveId.current ? resolveId.current + 1 : 1;
    const id = resolveId.current;
    (customResolve ? Promise.all([Promise.resolve(report), customResolve(report)]) : Promise.all([configure(report), loadResolve().then(({
      resolve
    }) => fixFilters(report, gates).then(configured => resolve(configured, Object.assign({}, runtimeOptions, {
      v2Datasets: true,
      fiscalYearMonthName
    }))))])).then(([nextConfiguredReport, nextReportData]) => {
      const onlySupportedViaBackendResolve = isOnlySupportedViaBackendResolve(report);
      nextConfiguredReport = (onlySupportedViaBackendResolve || runtimeOptions.useBackendResolve) && nextReportData.hasIn(['primary', 'config']) ? nextConfiguredReport.set('config', nextReportData.getIn(['primary', 'config'])) : nextConfiguredReport;
      if (id === resolveId.current) {
        const hasData = nextReportData.get('hasData') || nextReportData.keySeq().filter(nextReportDataKey => nextReportDataKey !== 'summary' && !nextReportData.getIn([nextReportDataKey, 'data', 'data'], List()).isEmpty()).count() > 0;
        if (onReportResolve) {
          onReportResolve({
            configured: nextConfiguredReport,
            data: nextReportData,
            hasData
          });
        }
        const nextReportState = {
          report,
          reportLoading: false,
          configuredReport: nextConfiguredReport,
          reportData: nextReportData,
          reportHasData: hasData
        };
        setReportState(nextReportState);
        setPreviousReportState(nextReportState);
        if (reportIngestion.current) {
          reportIngestion.current.logSuccess();
          reportIngestion.current = undefined;
        }
      }
    }).catch(e => {
      if (id === resolveId.current) {
        const nextReportState = {
          report,
          reportLoading: false,
          reportError: e
        };
        if (!reportIngestion.current) {
          const reportingLogger = new ReportingLogger();
          reportingLogger.addReportAttributes(report);
          reportingLogger.addAttribute('reportWithResolveError', e);
          reportingLogger.sendPageAction(REPORT_WITH_RESOLVE_ERROR_PAGEACTION);
          reportIngestion.current = new ReportingIngestionInteraction(report, {
            enabledForViz: isEnabledForViz(report)
          });
        }
        setReportState(nextReportState);
        setPreviousReportState(nextReportState);
        reportIngestion.current.logError(e);
        reportIngestion.current = undefined;
        if (onError) {
          onError(e);
        }
      }
    }).then(() => {
      if (typeof setIsRendered === 'function') {
        setIsRendered();
      }
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }, [report, currentReport, runtimeOptions, onError, onReportResolve, setIsRendered, customResolve, gates, fiscalYearMonthName]);
  if (!currentReport) {
    return null;
  }
  const {
    reportLoading: isLoading
  } = reportState;
  const {
    report: staleReport,
    reportLoading: wasLoading
  } = previousReportState;
  if (staleReport && isLoading && !wasLoading) {
    const {
      reportLoading,
      configuredReport,
      reportData,
      reportHasData,
      reportError
    } = previousReportState;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(ReportWithData, {
        report: staleReport,
        reportLoading: reportLoading,
        configuredReport: configuredReport,
        reportData: reportData,
        reportHasData: reportHasData,
        reportError: reportError,
        disableEmptyStates: disableEmptyStates,
        paginationRenderer: paginationRenderer,
        noDataRenderer: noDataRenderer,
        errorRenderer: errorRenderer,
        visualizationDebug: visualizationDebug,
        determineVisualization: determineVisualization,
        gates: gates,
        isMobile: isMobile,
        visualize: visualize,
        refreshReportData: refreshReportData,
        compact: compact,
        mobileReportingOptions: mobileReportingOptions,
        vizContainerStyleProps: vizContainerStyleProps,
        disableZoom: disableZoom
      }), /*#__PURE__*/_jsx(LoadingOverlay, {
        contextual: true
      })]
    });
  }
  const {
    reportLoading,
    configuredReport,
    reportData,
    reportHasData,
    reportError
  } = reportState;
  return /*#__PURE__*/_jsx(ReportWithData, {
    report: currentReport,
    reportLoading: reportLoading,
    configuredReport: configuredReport,
    reportData: reportData,
    reportHasData: reportHasData,
    reportError: reportError,
    disableEmptyStates: disableEmptyStates,
    paginationRenderer: paginationRenderer,
    noDataRenderer: noDataRenderer,
    errorRenderer: errorRenderer,
    visualizationDebug: visualizationDebug,
    determineVisualization: determineVisualization,
    gates: gates,
    isMobile: isMobile,
    visualize: visualize,
    refreshReportData: refreshReportData,
    compact: compact,
    mobileReportingOptions: mobileReportingOptions,
    vizContainerStyleProps: vizContainerStyleProps,
    disableZoom: disableZoom
  });
};
ReportWithResolve.propTypes = {
  report: PropTypes.object,
  runtimeOptions: PropTypes.object,
  disableEmptyStates: PropTypes.bool,
  paginationRenderer: PropTypes.func,
  noDataRenderer: PropTypes.func,
  errorRenderer: PropTypes.func,
  onReportResolve: PropTypes.func,
  onError: PropTypes.func,
  setIsRendered: PropTypes.func,
  refreshReportData: PropTypes.func,
  resolve: PropTypes.func,
  determineVisualization: PropTypes.func,
  isMobile: PropTypes.bool,
  compact: PropTypes.bool,
  /* For debugging v2 visualizations */
  visualizationDebug: PropTypes.object,
  visualize: PropTypes.func,
  disableZoom: PropTypes.bool
};
export default ReportWithResolve;